import { inject, Injectable } from '@angular/core';
import { BadgeConfig, MilestoneGroup, MilestoneGroupStatus } from '@models';
import { CommonFunctionsService } from '@services';

@Injectable({
  providedIn: 'root'
})
export class GroupTooltipService {
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);

  buildHTML(group: MilestoneGroup): string {
    return `
      <div class="study-gantt-chart-tooltip-container">
        <h3 class="study-gantt-chart-tooltip-title">${this.commonFunctionsService.toSentenceCase(group.name)}</h3>
        <div class="study-gantt-chart-tooltip-contents">
          ${this.getGroupStatus(group.status)}
          <p class="study-gantt-chart-tooltip-item">
            <span class="study-gantt-chart-tooltip-label">Start date</span>
            <span class="study-gantt-chart-tooltip-value">Baseline: ${this.commonFunctionsService.formatDate(group.startDates.baseline)}</span>
            <span class="study-gantt-chart-tooltip-value">Planned: ${this.commonFunctionsService.formatDate(group.startDates.planned)}</span>
            <span class="study-gantt-chart-tooltip-value">Actual: ${this.commonFunctionsService.formatDate(group.startDates.actual)}</span>
          </p>
          <p class="study-gantt-chart-tooltip-item">
            <span class="study-gantt-chart-tooltip-label">End date</span>
            <span class="study-gantt-chart-tooltip-value">Baseline: ${this.commonFunctionsService.formatDate(group.endDates.baseline)}</span>
            <span class="study-gantt-chart-tooltip-value">Planned: ${this.commonFunctionsService.formatDate(group.endDates.planned)}</span>
            <span class="study-gantt-chart-tooltip-value">Actual: ${this.commonFunctionsService.formatDate(group.endDates.actual)}</span>
          </p>
          <p class="study-gantt-chart-tooltip-item">
            <span class="study-gantt-chart-tooltip-value">
              <span class="study-gantt-chart-tooltip-highlight-value">Total duration: </span> 
              ${this.getFormattedTotalDuration(group.totalDuration)}
            </span>
          </p>
        </div>
      </div>
    `;
  }

  private getGroupStatus(status: MilestoneGroupStatus): string {
    const badge = this.getGroupBadge(status);
    return `
     <div class="study-gantt-chart-tooltip-status-item">
        <span class="study-gantt-chart-tooltip-label">Status:</span>
        <core-badge 
          color="${badge.textColor}" 
          backgroundcolor="${badge.backgroundColor}" 
          label="${badge.label}" 
          showicon="true"
        >
          <img slot="icon" src="assets/icons/statuses/muted/${badge.icon}" alt="${badge.label} icon"/>
        </core-badge>
      </div>`;
  }

  private getGroupBadge(status: MilestoneGroupStatus): BadgeConfig {
    const badgeMap: Record<string, BadgeConfig> = {
      Completed: {
        label: 'Completed',
        textColor: '#00399C',
        backgroundColor: '#DFEEFF',
        icon: 'completed.svg',
        iconColor: null
      },
      'In progress': {
        label: 'On track',
        textColor: '#005512',
        backgroundColor: '#D9FBE2',
        icon: 'on-track.svg',
        iconColor: null
      },
      'At risk': {
        label: 'At risk',
        textColor: '#6C2501',
        backgroundColor: '#FFEBD6',
        icon: 'at-risk.svg',
        iconColor: null
      },
      'Off track': {
        label: 'Off track',
        textColor: '#790000',
        backgroundColor: '#FDE4E4',
        icon: 'past-due.svg',
        iconColor: null
      },
      Upcoming: {
        label: 'Upcoming',
        textColor: '#2B2B30',
        backgroundColor: '#E5E5E5',
        icon: 'upcoming.svg',
        iconColor: null
      }
    };

    return status ? badgeMap[status] : badgeMap['Upcoming'];
  }

  private getFormattedTotalDuration(totalDuration: number | null): string {
    if (totalDuration === null) {
      return '--';
    }
    const daysLabel = totalDuration === 1 ? 'day' : 'days';

    return `${totalDuration} ${daysLabel}`;
  }
}
